import React from 'react';
import './PriceDisplay.css';

export type PriceDisplaySize = 'sm' | 'md' | 'lg' | 'xl';

export interface PriceDisplayProps {
  /** The price amount to display */
  amount: number;
  /** Currency symbol (default: '£') */
  currency?: string;
  /** Billing interval (e.g., 'month', 'year', 'once') */
  interval?: 'month' | 'year' | 'once' | string;
  /** Original price for showing discounts (displayed with strikethrough) */
  originalAmount?: number;
  /** Size variant */
  size?: PriceDisplaySize;
  /** VAT text to display (e.g., '+ VAT', 'inc. VAT') */
  vatText?: string;
  /** Interval label format: 'short' (/mo, /yr) or 'long' (/month, /year) */
  intervalFormat?: 'short' | 'long';
  /** Custom CSS class */
  className?: string;
  /** Inline styles (useful for CSS variable overrides) */
  style?: React.CSSProperties;
}

/**
 * Formats a number as a price string with appropriate decimal places.
 * Shows decimals only when needed (e.g., £10 vs £10.99)
 */
const formatPrice = (amount: number): string => {
  // If whole number, show no decimals
  if (Number.isInteger(amount)) {
    return amount.toString();
  }
  // Otherwise show 2 decimal places
  return amount.toFixed(2);
};

/**
 * Gets the display text for an interval
 */
const getIntervalText = (interval: string, format: 'short' | 'long' = 'short'): string => {
  switch (interval) {
    case 'month':
      return format === 'long' ? '/month' : '/mo';
    case 'year':
      return format === 'long' ? '/year' : '/yr';
    case 'once':
      return '';
    default:
      return interval.startsWith('/') ? interval : `/${interval}`;
  }
};

export const PriceDisplay: React.FC<PriceDisplayProps> = ({
  amount,
  currency = '£',
  interval,
  originalAmount,
  size = 'md',
  vatText,
  intervalFormat = 'short',
  className = '',
  style,
}) => {
  const classes = [
    'hi-ui-price-display',
    `hi-ui-price-display--${size}`,
    originalAmount !== undefined ? 'hi-ui-price-display--has-discount' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const hasDiscount = originalAmount !== undefined && originalAmount > amount;
  const intervalText = interval ? getIntervalText(interval, intervalFormat) : '';

  return (
    <span className={classes} style={style}>
      {hasDiscount && (
        <span
          className="hi-ui-price-display__original"
          aria-label={`Was ${currency}${formatPrice(originalAmount)}`}
        >
          <span className="hi-ui-price-display__currency">{currency}</span>
          <span className="hi-ui-price-display__amount">{formatPrice(originalAmount)}</span>
        </span>
      )}
      <span className="hi-ui-price-display__current">
        <span className="hi-ui-price-display__currency">{currency}</span>
        <span className="hi-ui-price-display__amount">{formatPrice(amount)}</span>
        {intervalText && <span className="hi-ui-price-display__interval">{intervalText}</span>}
      </span>
      {vatText && <span className="hi-ui-price-display__vat">{vatText}</span>}
    </span>
  );
};
